import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Alert from "../../../Common/Alert";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import {
  ChartOfAccountStyles,
  formatDate,
  money,
  num,
} from "../../Accounts/Chart-Of-Account/ChartOfAccountShared";
import {
  FinancialStatementStyles,
  PresetChips,
  amount,
  endOfLastMonth,
  endOfLastYear,
  printStatement,
  today,
} from "./FinancialStatementShared";

// Supplier Advance and Supplier Payable are the same report read from
// opposite sides of the supplier's ledger.
const MODES = {
  advance: {
    title: "Supplier Advance",
    endpoint: "/supplier-advance",
    balance: "Advance",
    subtitle: "Suppliers paid more than they have billed",
    rule: "debits exceed their credits",
    totalIcon: "fa-forward",
    empty: "No supplier has an advance outstanding",
    canShowAll: false,
  },
  payable: {
    title: "Supplier Payable",
    endpoint: "/supplier-payable",
    balance: "Payable",
    subtitle: "Suppliers billed more than they have been paid",
    rule: "credits exceed their debits",
    totalIcon: "fa-credit-card",
    empty: "Nothing is owed to any supplier",
    canShowAll: true,
  },
};

const SORTS = [
  ["balance", "Largest balance"],
  ["name", "Supplier name"],
  ["last_date", "Most recent entry"],
];

// Vendor names were saved HTML-escaped by the old system ("A &amp; B").
export const decode = (text) => {
  if (!text || !/&[#a-z0-9]+;/i.test(text)) return text;
  const doc = new DOMParser().parseFromString(text, "text/html");
  return doc.documentElement.textContent;
};

export const escapeHtml = (text) =>
  String(text ?? "").replace(
    /[&<>"]/g,
    (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[ch],
  );

export const csvCell = (value) => {
  const text = value === null || value === undefined ? "" : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const daysBetween = (from, to) => {
  const parse = (value) => {
    const [y, m, d] = value.split("-").map(Number);
    return new Date(y, m - 1, d);
  };
  return Math.round((parse(to) - parse(from)) / (24 * 60 * 60 * 1000));
};

export const SbStyles = () => (
  <style>
    {`
      .sb-table { width: 100%; margin: 0; border-collapse: collapse; }
      .sb-table thead th {
        padding: 10px 12px;
        background: #2a2962;
        color: #fff;
        font-size: 0.7rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        white-space: nowrap;
        border: 0;
      }
      .sb-table thead th.coa-num { text-align: right; }
      .sb-table > tbody > tr > td {
        padding: 9px 12px;
        border-bottom: 1px solid #f0f1f6;
        font-size: 0.85rem;
        vertical-align: middle;
      }
      .sb-table td.coa-num { white-space: nowrap; }
      .sb-table > tbody > tr.sb-row:hover > td { background: #fafbfd; }
      .sb-table > tbody > tr.sb-row.is-open > td { background: #f7f8fc; }
      .sb-table tfoot td {
        padding: 11px 12px;
        background: #2a2962;
        color: #fff;
        font-weight: 700;
      }
      .sb-sl { width: 48px; color: #8b90a5; font-size: 0.75rem; }
      .sb-name-btn {
        padding: 0;
        border: 0;
        background: none;
        font-weight: 600;
        color: #2a2962;
        text-align: left;
        cursor: pointer;
      }
      .sb-name-btn:hover { color: #e42320; }
      .sb-vendor-link { margin-left: 6px; color: #b6bacb; font-size: 0.75rem; }
      .sb-vendor-link:hover { color: #2a2962; }
      .sb-sub { display: block; font-size: 0.7rem; color: #8b90a5; }
      .sb-neg { color: #e42320; }
      .sb-settled { color: #b6bacb; }
      .sb-share { display: flex; align-items: center; gap: 8px; min-width: 140px; }
      .sb-share-bar { flex: 1; height: 6px; border-radius: 3px; background: #f2f3f9; overflow: hidden; }
      .sb-share-fill { height: 100%; border-radius: 3px; background: #2a2962; }
      .sb-share-pct { width: 44px; text-align: right; font-size: 0.72rem; color: #52566b; }
      .sb-age {
        display: inline-block;
        padding: 1px 7px;
        border-radius: 8px;
        font-size: 0.66rem;
        font-weight: 600;
        white-space: nowrap;
      }
      .sb-age.is-fresh { background: #e8f6ee; color: #1f9d55; }
      .sb-age.is-aging { background: #fdf3e2; color: #c77700; }
      .sb-age.is-stale { background: #fdecec; color: #e42320; }
      .sb-tools { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
      .sb-tools .form-control { height: 32px; font-size: 0.8rem; width: auto; }
      .sb-search { position: relative; min-width: 200px; }
      .sb-search i {
        position: absolute; top: 50%; left: 10px;
        transform: translateY(-50%); color: #b6bacb;
      }
      .sb-search .form-control { padding-left: 30px; width: 100%; }

      .sb-ledger-cell { padding: 0 12px 14px 60px !important; background: #f7f8fc; }
      .sb-ledger {
        width: 100%;
        border-collapse: collapse;
        background: #fff;
        border: 1px solid #e6e8f0;
        border-radius: 6px;
      }
      .sb-ledger th {
        padding: 7px 10px;
        background: #eef0f8;
        color: #52566b;
        font-size: 0.66rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        white-space: nowrap;
      }
      .sb-ledger th.coa-num { text-align: right; }
      .sb-ledger td {
        padding: 6px 10px;
        border-top: 1px solid #f0f1f6;
        font-size: 0.78rem;
        color: #35374d;
      }
      .sb-ledger a { color: #2a2962; font-weight: 600; }
      .sb-ledger a:hover { color: #e42320; text-decoration: underline; }
      .sb-ledger-note { padding: 10px; font-size: 0.78rem; color: #8b90a5; }
    `}
  </style>
);

export const AgeBadge = ({ lastDate, asOf }) => {
  if (!lastDate) return null;
  const days = daysBetween(lastDate, asOf);
  const state = days <= 30 ? "is-fresh" : days <= 90 ? "is-aging" : "is-stale";
  const label =
    days <= 0 ? "Today" : days === 1 ? "1 day ago" : `${days} days ago`;
  return (
    <span className={`sb-age ${state}`} title="Time since the last entry">
      {label}
    </span>
  );
};

// A supplier's entries up to the statement date, loaded when the row is
// opened.
export const SupplierLedger = ({ ledger }) => {
  if (!ledger || ledger.loading) {
    return (
      <div className="sb-ledger-note">
        <i className="fa fa-spinner fa-spin mg-r-6"></i>Loading entries...
      </div>
    );
  }
  if (ledger.error) {
    return <div className="sb-ledger-note sb-neg">{ledger.error}</div>;
  }
  if (!ledger.entries.length) {
    return <div className="sb-ledger-note">No entries.</div>;
  }
  return (
    <table className="sb-ledger">
      <thead>
        <tr>
          <th>Date</th>
          <th>Voucher</th>
          <th>Account</th>
          <th>Remarks</th>
          <th className="coa-num">Debit</th>
          <th className="coa-num">Credit</th>
          <th className="coa-num">Balance</th>
        </tr>
      </thead>
      <tbody>
        {ledger.entries.map((entry) => (
          <tr key={entry.id}>
            <td style={{ whiteSpace: "nowrap" }}>{formatDate(entry.date)}</td>
            <td style={{ whiteSpace: "nowrap" }}>
              <Link to={`/journal-voucher-details/${entry.voucher_id}`}>
                {entry.voucher_no}
              </Link>
              <span className="sb-sub" style={{ textTransform: "capitalize" }}>
                {entry.voucher_type}
              </span>
            </td>
            <td>
              {entry.account_code} - {entry.account_name}
            </td>
            <td>{entry.remarks}</td>
            <td className="coa-num">{num(entry.debit) ? money(entry.debit) : ""}</td>
            <td className="coa-num">{num(entry.credit) ? money(entry.credit) : ""}</td>
            <td
              className={`coa-num ${num(entry.balance) < 0 ? "sb-neg" : ""}`}
              style={{ fontWeight: 600 }}
            >
              {amount(entry.balance)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const SupplierBalanceReport = ({ mode }) => {
  const config = MODES[mode];
  const [searchParams, setSearchParams] = useSearchParams();
  const [asOf, setAsOf] = useState(searchParams.get("as_of") || today());
  const [showAll, setShowAll] = useState(
    config.canShowAll && searchParams.get("show") === "all",
  );
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("balance");
  const [open, setOpen] = useState({});
  const [ledgers, setLedgers] = useState({});

  const fetchData = async (date = asOf, all = showAll) => {
    setLoading(true);
    setAlert(null);
    setOpen({});
    setLedgers({});

    const query = { as_of: date };
    if (all) query.show = "all";
    setSearchParams(query, { replace: true });

    try {
      const res = await Http.get(config.endpoint, { params: query });
      setData(res.data);
    } catch (error) {
      console.error("Fetch error:", error);
      setData(null);
      setAlert({
        message:
          error.response?.data?.message ||
          "Could not load the report. Please try again.",
        type: "danger",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const applyDate = (date) => {
    setAsOf(date);
    fetchData(date);
  };

  const toggleShowAll = (value) => {
    setShowAll(value);
    fetchData(asOf, value);
  };

  const toggleSupplier = (row) => {
    const isOpen = !open[row.id];
    setOpen((values) => ({ ...values, [row.id]: isOpen }));
    if (!isOpen || ledgers[row.id]) return;

    setLedgers((values) => ({ ...values, [row.id]: { loading: true } }));
    Http.get("/supplier-balance-ledger", {
      params: { report: mode, supplier_id: row.id, as_of: data.as_of },
    })
      .then((res) =>
        setLedgers((values) => ({
          ...values,
          [row.id]: { entries: res.data.entries || [] },
        })),
      )
      .catch((error) =>
        setLedgers((values) => ({
          ...values,
          [row.id]: {
            error:
              error.response?.data?.message || "Could not load the entries.",
          },
        })),
      );
  };

  const outstanding = data ? num(data.totals.outstanding) : 0;

  const rows = useMemo(() => {
    const list = data ? [...data.rows] : [];
    const query = search.trim().toLowerCase();
    const filtered = list.filter(
      (row) =>
        !query ||
        decode(`${row.code || ""} ${row.name} ${row.contact_person || ""}`)
          .toLowerCase()
          .includes(query),
    );
    const compare = {
      balance: (a, b) => num(b.balance) - num(a.balance),
      name: (a, b) => decode(a.name).localeCompare(decode(b.name)),
      last_date: (a, b) => (b.last_date || "").localeCompare(a.last_date || ""),
    }[sort];
    return filtered.sort(compare);
  }, [data, search, sort]);

  const totals = useMemo(
    () => ({
      debit: rows.reduce((sum, row) => sum + num(row.debit), 0),
      credit: rows.reduce((sum, row) => sum + num(row.credit), 0),
      balance: rows.reduce((sum, row) => sum + num(row.balance), 0),
    }),
    [rows],
  );

  const owing = data ? data.rows.filter((row) => num(row.balance) > 0.004) : [];

  const largest = owing.length
    ? owing.reduce((top, row) => (num(row.balance) > num(top.balance) ? row : top))
    : null;

  const staleCount = data
    ? owing.filter(
        (row) => row.last_date && daysBetween(row.last_date, data.as_of) > 90,
      ).length
    : 0;

  const share = (value) =>
    outstanding > 0 && num(value) > 0 ? (num(value) * 100) / outstanding : 0;

  const scopeLabel =
    data && data.accounts.length
      ? data.accounts.map((a) => `${a.code} - ${a.name}`).join(", ")
      : "all accounts";

  const exportCsv = () => {
    const lines = [
      [config.title],
      [`As at ${data.as_of}${data.show === "all" ? " (all suppliers)" : ""}`],
      [],
      ["Sl", "Code", "Supplier", "Contact", "Debit", "Credit", config.balance, "Share %", "Entries", "Last Entry"],
      ...rows.map((row, i) => [
        i + 1,
        row.code,
        decode(row.name),
        [row.contact_person, row.contact_phone].filter(Boolean).join(" "),
        num(row.debit).toFixed(2),
        num(row.credit).toFixed(2),
        num(row.balance).toFixed(2),
        share(row.balance).toFixed(2),
        row.entries,
        row.last_date,
      ]),
      [],
      ["", "", "Total", "", totals.debit.toFixed(2), totals.credit.toFixed(2), totals.balance.toFixed(2)],
    ];
    const csv = lines.map((line) => line.map(csvCell).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${mode === "advance" ? "supplier-advance" : "supplier-payable"}_${data.as_of}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    const headHtml = `
      <th>Sl</th><th>Code</th><th>Supplier</th>
      <th class="num">Debit</th><th class="num">Credit</th>
      <th class="num">${config.balance}</th><th class="num">Share</th><th>Last Entry</th>`;
    const bodyHtml =
      rows
        .map(
          (row, i) => `
        <tr>
          <td>${i + 1}</td>
          <td class="code">${escapeHtml(row.code)}</td>
          <td>${escapeHtml(decode(row.name))}</td>
          <td class="num">${money(row.debit)}</td>
          <td class="num">${money(row.credit)}</td>
          <td class="num">${amount(row.balance)}</td>
          <td class="num">${share(row.balance) ? `${share(row.balance).toFixed(1)}%` : ""}</td>
          <td>${formatDate(row.last_date)}</td>
        </tr>`,
        )
        .join("") +
      `<tr class="grand">
        <td colspan="3">Total</td>
        <td class="num">${money(totals.debit)}</td>
        <td class="num">${money(totals.credit)}</td>
        <td class="num">${amount(totals.balance)}</td>
        <td colspan="2"></td>
      </tr>`;

    printStatement({
      title: config.title,
      subtitle: `As at ${formatDate(data.as_of)}${
        data.show === "all" ? " &middot; All suppliers" : ""
      } &middot; Amounts in Taka`,
      headHtml,
      bodyHtml,
      note:
        data.show === "all"
          ? `Every supplier with postings on ${scopeLabel} up to the date. Amounts in brackets are the other way round (overpaid).`
          : `Suppliers whose ${config.rule} on ${scopeLabel}, counting every posting up to the date.`,
    });
  };

  const stat = (label, icon, value, sub, className = "") => (
    <div className="col-md-3 mg-b-10">
      <div className={`coa-stat ${className}`}>
        <i className={`fa ${icon} coa-stat-icon`}></i>
        <span className="coa-stat-label">{label}</span>
        <span className="coa-stat-value">{value}</span>
        <span className="coa-stat-sub">{sub}</span>
      </div>
    </div>
  );

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <ChartOfAccountStyles />
        <FinancialStatementStyles />
        <SbStyles />
        <div className="am-pagetitle">
          <div>
            <h5 className="am-title mb-1">{config.title}</h5>
            <p className="text-muted mb-0 tx-12">
              {config.subtitle}, as at {formatDate(data?.as_of || asOf)}
            </p>
          </div>
          <div className="d-flex gap-2 coa-no-print">
            <Link to="/reports" className="btn btn-outline-secondary">
              <i className="fa fa-arrow-left mg-r-6"></i>Reports
            </Link>
            <button
              className="btn btn-outline-secondary"
              onClick={() => fetchData()}
              title="Reload data"
              disabled={loading}
            >
              <i className="fa fa-refresh"></i>
            </button>
            <button
              className="btn btn-outline-secondary"
              onClick={exportCsv}
              disabled={loading || !rows.length}
            >
              <i className="fa fa-file-excel-o mg-r-6"></i>Export CSV
            </button>
            <button
              className="btn btn-primary"
              onClick={handlePrint}
              disabled={loading || !data}
            >
              <i className="fa fa-print mg-r-6"></i>Print
            </button>
          </div>
        </div>

        <div className="am-pagebody">
          <Alert alert={alert} />

          <div className="coa-panel coa-no-print">
            <div className="coa-panel-head">
              <span>
                <i className="fa fa-calendar-o me-2"></i>Statement Date
              </span>
              <span className="coa-head-note">
                Every supplier posting up to the date, on {scopeLabel}
              </span>
            </div>
            <div className="coa-panel-body">
              <div className="row align-items-start">
                <div className="col-lg-4">
                  <label>As at</label>
                  <input
                    type="date"
                    className="form-control"
                    value={asOf}
                    onChange={(e) => setAsOf(e.target.value)}
                  />
                  <PresetChips
                    presets={[
                      ["Today", today()],
                      ["Last month end", endOfLastMonth()],
                      ["Last year end", endOfLastYear()],
                    ]}
                    isActive={([, value]) => asOf === value}
                    onPick={([, value]) => applyDate(value)}
                  />
                </div>
                <div className="col-lg-2 mg-t-10 mg-lg-t-0">
                  <label>&nbsp;</label>
                  <button
                    className="btn btn-primary"
                    onClick={() => fetchData()}
                    disabled={loading || !asOf}
                  >
                    <i
                      className={`fa ${loading ? "fa-spinner fa-spin" : "fa-search"} mg-r-6`}
                    ></i>
                    {loading ? "Loading..." : "Apply"}
                  </button>
                </div>
                {config.canShowAll && (
                  <div className="col-lg-4 mg-t-10 mg-lg-t-0">
                    <label>Suppliers</label>
                    <PresetChips
                      presets={[
                        ["Payable only", false],
                        ["All suppliers", true],
                      ]}
                      isActive={([, value]) => showAll === value}
                      onPick={([, value]) => toggleShowAll(value)}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {data && (
            <div className="row mg-b-20">
              {stat(
                `Total ${config.balance}`,
                config.totalIcon,
                money(outstanding),
                `${owing.length} supplier${owing.length === 1 ? "" : "s"}`,
              )}
              {stat(
                "Debit / Credit",
                "fa-exchange",
                money(data.totals.debit),
                `Debit · credit ${money(data.totals.credit)}`,
              )}
              {stat(
                `Largest ${config.balance}`,
                "fa-arrow-up",
                largest ? money(largest.balance) : "–",
                largest
                  ? `${decode(largest.name)} · ${share(largest.balance).toFixed(1)}% of total`
                  : "–",
              )}
              {stat(
                "Idle Over 90 Days",
                "fa-clock-o",
                staleCount,
                staleCount
                  ? "No entry for more than 90 days"
                  : "Every balance has recent activity",
                staleCount ? "is-alert" : "is-ok",
              )}
            </div>
          )}

          <div className="coa-panel">
            <div className="coa-panel-head">
              <span>
                <i className="fa fa-table me-2"></i>Supplier Balances
                <span className="coa-head-note mg-l-10">
                  Click a supplier to see its entries
                </span>
              </span>
              {data && data.rows.length > 0 && (
                <span className="sb-tools coa-no-print">
                  <select
                    className="form-control"
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    aria-label="Sort by"
                  >
                    {SORTS.map(([value, label]) => (
                      <option key={value} value={value}>
                        Sort: {label}
                      </option>
                    ))}
                  </select>
                  <div className="sb-search">
                    <i className="fa fa-search" aria-hidden="true"></i>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search supplier"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      aria-label="Search supplier"
                    />
                  </div>
                </span>
              )}
            </div>

            {loading && !data ? (
              <div className="pd-y-10">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="fs-skeleton"></div>
                ))}
              </div>
            ) : !data ? (
              <div className="coa-empty">
                <i className="fa fa-exclamation-circle"></i>
                The report could not be loaded.
              </div>
            ) : !rows.length ? (
              <div className="coa-empty">
                <i className="fa fa-check-circle-o"></i>
                {data.rows.length
                  ? "No suppliers match the search."
                  : `${config.empty} as at ${formatDate(data.as_of)}.`}
              </div>
            ) : (
              <div className="table-responsive">
                <table className="sb-table">
                  <thead>
                    <tr>
                      <th>Sl</th>
                      <th>Code</th>
                      <th>Supplier</th>
                      <th className="coa-num">Debit</th>
                      <th className="coa-num">Credit</th>
                      <th className="coa-num">{config.balance}</th>
                      <th>Share of Total</th>
                      <th>Last Entry</th>
                    </tr>
                  </thead>
                  <tbody style={{ opacity: loading ? 0.5 : 1 }}>
                    {rows.map((row, i) => {
                      const balance = num(row.balance);
                      const isOpen = !!open[row.id];
                      return (
                        <React.Fragment key={row.id}>
                          <tr className={`sb-row${isOpen ? " is-open" : ""}`}>
                            <td className="sb-sl">{i + 1}</td>
                            <td>
                              {row.code && (
                                <span className="coa-code">{row.code}</span>
                              )}
                            </td>
                            <td>
                              <button
                                type="button"
                                className="sb-name-btn"
                                onClick={() => toggleSupplier(row)}
                                aria-expanded={isOpen}
                              >
                                <i
                                  className={`fa fa-caret-${isOpen ? "down" : "right"} mg-r-6`}
                                  aria-hidden="true"
                                ></i>
                                {decode(row.name)}
                              </button>
                              <Link
                                to={`/vendor-details/${row.id}`}
                                className="sb-vendor-link"
                                title="Open the supplier"
                              >
                                <i className="fa fa-external-link"></i>
                              </Link>
                              {(row.contact_person || row.contact_phone) && (
                                <span className="sb-sub">
                                  {[row.contact_person, row.contact_phone]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </span>
                              )}
                            </td>
                            <td className="coa-num">{money(row.debit)}</td>
                            <td className="coa-num">{money(row.credit)}</td>
                            <td
                              className={`coa-num ${
                                balance < -0.004
                                  ? "sb-neg"
                                  : Math.abs(balance) <= 0.004
                                    ? "sb-settled"
                                    : ""
                              }`}
                              style={{ fontWeight: 700, color: balance > 0.004 ? "#2a2962" : undefined }}
                              title={
                                balance < -0.004
                                  ? mode === "payable"
                                    ? "Overpaid: this is an advance"
                                    : "Owed to the supplier"
                                  : undefined
                              }
                            >
                              {amount(row.balance)}
                            </td>
                            <td>
                              {share(row.balance) > 0 && (
                                <div className="sb-share">
                                  <div className="sb-share-bar">
                                    <div
                                      className="sb-share-fill"
                                      style={{ width: `${share(row.balance)}%` }}
                                    ></div>
                                  </div>
                                  <span className="sb-share-pct">
                                    {share(row.balance).toFixed(1)}%
                                  </span>
                                </div>
                              )}
                            </td>
                            <td style={{ whiteSpace: "nowrap" }}>
                              {formatDate(row.last_date)}
                              <span className="sb-sub">
                                <AgeBadge lastDate={row.last_date} asOf={data.as_of} />{" "}
                                {row.entries} entr{row.entries === 1 ? "y" : "ies"}
                              </span>
                            </td>
                          </tr>
                          {isOpen && (
                            <tr>
                              <td colSpan={8} className="sb-ledger-cell">
                                <SupplierLedger ledger={ledgers[row.id]} />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={3} style={{ textAlign: "right" }}>
                        Total
                      </td>
                      <td className="coa-num">{money(totals.debit)}</td>
                      <td className="coa-num">{money(totals.credit)}</td>
                      <td className="coa-num">{amount(totals.balance)}</td>
                      <td colSpan={2}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplierBalanceReport;
